import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { selectCohortDefinitionIsVersionLoading } from '@verantos-dev/app-state-cohort-definition';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { CohortBuilderModalLegacy, CohortStatusLegacy } from '@verantos-dev/types-cohorts';

import { useCohortBuilderItemWithInUseItems } from './useCohortBuilderItemWithInUseItems';
import { useCohortEditable } from './useCohortEditable';
import { setModal, deleteDerivedEvent } from '../cohortBuilderSlice';
import { selectModal } from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDefinition,
  selectDerivedEvents,
  selectSimpleEvents,
} from '../selectors/cohortDefinitionSelectors';
import { getFullCohortDslForDeleteDerivedEvent } from '../utils/dsl-generators/getFullCohortDslForDerivedEvent';

export const useDerivedEventView = () => {
  const dispatch = useAppDispatch();

  const { data: cohortData } = useCohortBuilderData();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortStatusLegacy.PROCESSING, CohortStatusLegacy.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;

  // select the index date and modal from the store
  const modal = useAppSelector(selectModal);
  const currentCohortDefinition = useAppSelector(selectCohortDefinition);
  /** Cohort Builder Definition State */
  const simpleEvents = useAppSelector(selectSimpleEvents);
  const derivedEvents = useAppSelector(selectDerivedEvents);
  const isLoading = useAppSelector(selectCohortDefinitionIsVersionLoading);
  const derivedEventsWithInUseItems = useCohortBuilderItemWithInUseItems(derivedEvents);

  const showDerivedEventEditModal =
    modal !== undefined &&
    [
      CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
      CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
    ].includes(modal);
  const isAddDerivedEventDisabled =
    modal !== undefined || !simpleEvents || simpleEvents.length === 0;
  const disabledAddDerivedEventTooltipText =
    !simpleEvents || simpleEvents.length === 0 ? 'Add a Simple Event first' : undefined;

  /** Click Handlers */

  // open the modal to add a Derived event
  const onAddDerivedEventClick = () => {
    dispatch(
      setModal({
        id: uuidv4(),
        modal: CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
      })
    );
  };

  const onEditDerivedEventClick = (id: string) => {
    dispatch(
      setModal({
        id,
        modal: CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
      })
    );
  };

  const onDeleteDerivedEventClick =
    (emitResearchAssistantUpdate: (nextCohortDsl: string) => void) => (id: string) => {
      if (!viewMode) {
        dispatch(deleteDerivedEvent(id));
        const nextCohortDsl = getFullCohortDslForDeleteDerivedEvent(currentCohortDefinition, id);
        emitResearchAssistantUpdate(nextCohortDsl.fullDslQueryString);
      }
    };

  return {
    derivedEvents: derivedEventsWithInUseItems,
    showDerivedEventEditModal,
    isAddDerivedEventDisabled,
    disabledAddDerivedEventTooltipText,
    onAddDerivedEventClick,
    onEditDerivedEventClick,
    onDeleteDerivedEventClick,
    isEditable,
    isLoading,
    viewMode,
  };
};
